import React from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import Combobox from "@/components/Combobox";
import { cn } from "@/lib/utils";

/**
 * Renders a single game row. Four states:
 *  - filled: answered — green (relevant) or red (irrelevant)
 *  - active: current slot — inline searchable dropdown that fires guess on pick
 *  - locked: future empty slot (dashed, muted)
 */
export default function GameRow({
  index,
  row,
  active = false,
  items = [],
  onPick,
  loading = false,
}) {
  // Filled slot — an answered question
  if (row) {
    const positive = row.relevant === true;
    return (
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        data-testid={`game-row-filled-${index}`}
        className={cn(
          "flex items-center justify-between gap-4 rounded-lg px-4 py-4 min-h-[64px]",
          positive ? "bg-emerald-500" : "bg-red-500"
        )}
      >
        <span className="text-white text-sm sm:text-base font-medium leading-snug">
          {row.text}
        </span>
        <span className="shrink-0 rounded-full bg-white/15 backdrop-blur-sm text-white text-xs font-mono tracking-widest uppercase px-3 py-1">
          {positive ? "Yes" : "No"}
        </span>
      </motion.div>
    );
  }

  // Active slot — the current question the player is picking
  if (active) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2 }}
        data-testid={`game-row-active-${index}`}
        className="rounded-lg border-2 border-slate-900 bg-white p-2 sm:p-2.5 min-h-[64px] flex items-center gap-2 sm:gap-3 shadow-[0_2px_0_rgba(15,23,42,0.06)]"
      >
        <span className="shrink-0 text-[10px] sm:text-xs font-mono tracking-[0.15em] uppercase text-slate-500 pl-1 sm:pl-2">
          Q{index + 1}
        </span>
        <div className="flex-1 min-w-0">
          <Combobox
            items={items}
            value=""
            onSelect={(v) => onPick && onPick(Number(v))}
            placeholder={loading ? "Submitting…" : "Pick a question…"}
            triggerTestId={`question-slot-${index}-trigger`}
            itemTestIdPrefix={`question-slot-${index}-item`}
            disabled={loading}
          />
        </div>
        {loading && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-500 mr-1" />}
      </motion.div>
    );
  }

  // Locked / future empty slot
  return (
    <div
      data-testid={`game-row-empty-${index}`}
      className="flex items-center justify-between rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 px-4 py-4 min-h-[64px]"
    >
      <span className="text-slate-400 text-sm font-medium tracking-wide">
        Question {index + 1}
      </span>
      <span className="text-slate-300 text-xs font-mono">—</span>
    </div>
  );
}
